import { useEffect, useMemo, useState } from "react";
import {
  getAllTopics,
  getTopicLabels,
  getTopic,
  createTopic,
  regenerateTopic,
  updateTopic,
  deleteTopic,
} from "./TopicsService";
import { useToast } from "../ToastContext";
import "./TopicsPage.css";

const LABEL_COLORS = [
  "#6366f1", "#22c55e", "#f59e0b", "#ec4899", "#06b6d4",
  "#a855f7", "#ef4444", "#14b8a6", "#eab308", "#3b82f6",
];

function colorForLabel(label) {
  if (!label) return LABEL_COLORS[0];
  let hash = 0;
  for (let i = 0; i < label.length; i++) hash = (hash * 31 + label.charCodeAt(i)) >>> 0;
  return LABEL_COLORS[hash % LABEL_COLORS.length];
}

function renderInline(text, keyPrefix) {
  const parts = String(text).split(/(\*\*[^*]+\*\*|`[^`]+`)/g).filter(Boolean);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={`${keyPrefix}-${i}`}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith("`") && part.endsWith("`")) {
      return <code key={`${keyPrefix}-${i}`}>{part.slice(1, -1)}</code>;
    }
    return <span key={`${keyPrefix}-${i}`}>{part}</span>;
  });
}

function LiteMarkdown({ text }) {
  const blocks = useMemo(() => {
    if (!text) return [];
    const lines = text.split("\n");
    const out = [];
    let listBuffer = [];
    let listType = null;

    const flushList = () => {
      if (listBuffer.length === 0) return;
      const items = listBuffer.map((item, i) => <li key={i}>{renderInline(item, `li-${out.length}-${i}`)}</li>);
      out.push(
        listType === "ol"
          ? <ol key={out.length}>{items}</ol>
          : <ul key={out.length}>{items}</ul>
      );
      listBuffer = [];
      listType = null;
    };

    lines.forEach((raw) => {
      const trimmed = raw.trim();
      if (!trimmed) { flushList(); return; }
      const headerMatch = trimmed.match(/^(#{1,4})\s+(.*)$/);
      const bulletMatch = trimmed.match(/^[-*]\s+(.*)$/);
      const numberMatch = trimmed.match(/^\d+[.)]\s+(.*)$/);
      if (headerMatch) {
        flushList();
        const level = Math.min(headerMatch[1].length + 3, 6);
        const Tag = `h${level}`;
        out.push(<Tag key={out.length}>{renderInline(headerMatch[2], `h-${out.length}`)}</Tag>);
      } else if (bulletMatch) {
        if (listType !== "ul") { flushList(); listType = "ul"; }
        listBuffer.push(bulletMatch[1]);
      } else if (numberMatch) {
        if (listType !== "ol") { flushList(); listType = "ol"; }
        listBuffer.push(numberMatch[1]);
      } else {
        flushList();
        out.push(<p key={out.length}>{renderInline(trimmed, `p-${out.length}`)}</p>);
      }
    });
    flushList();
    return out;
  }, [text]);

  if (!text) return <div className="topics-empty-tab">Nothing here yet.</div>;
  return <div className="topics-markdown">{blocks}</div>;
}

function timeAgo(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function TopicsPage() {
  const showToast = useToast();

  const [topics, setTopics] = useState([]);
  const [labels, setLabels] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  const [selectedId, setSelectedId] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [activeTab, setActiveTab] = useState("explanation");

  const [filterLabel, setFilterLabel] = useState("All");
  const [search, setSearch] = useState("");

  const [showNewModal, setShowNewModal] = useState(false);
  const [newTopicText, setNewTopicText] = useState("");
  const [newTopicLabel, setNewTopicLabel] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoadingList(true);
    try {
      const [t, l] = await Promise.all([getAllTopics(), getTopicLabels()]);
      setTopics(t || []);
      setLabels(l || []);
    } catch (err) {
      showToast(err?.message || "Failed to load topics.", "error");
    } finally {
      setLoadingList(false);
    }
  };

  const openTopic = async (id) => {
    setSelectedId(id);
    setActiveTab("explanation");
    setLoadingDetail(true);
    try {
      const full = await getTopic(id);
      setSelectedTopic(full);
    } catch (err) {
      showToast(err?.message || "Failed to load topic.", "error");
      setSelectedTopic(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  const filteredTopics = useMemo(() => {
    return topics.filter((t) => {
      if (filterLabel !== "All" && t.label !== filterLabel) return false;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        if (!t.title?.toLowerCase().includes(q) && !t.summary?.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [topics, filterLabel, search]);

  const handleCreate = async () => {
    if (!newTopicText.trim()) return;
    setIsCreating(true);
    try {
      const created = await createTopic({
        topic: newTopicText.trim(),
        label: newTopicLabel.trim() || undefined,
      });
      showToast(`"${created.title}" is ready.`, "success");
      setShowNewModal(false);
      setNewTopicText("");
      setNewTopicLabel("");
      await load();
      setSelectedId(created.id);
      setSelectedTopic(created);
      setActiveTab("explanation");
    } catch (err) {
      const msg = err?.message || (err?.text ? await err.text() : null) || "Failed to generate this topic.";
      showToast(typeof msg === "string" ? msg : "Failed to generate this topic.", "error");
    } finally {
      setIsCreating(false);
    }
  };

  const handleRegenerate = async () => {
    if (!selectedId) return;
    setIsRegenerating(true);
    try {
      const updated = await regenerateTopic(selectedId);
      setSelectedTopic(updated);
      await load();
      showToast("Regenerated.", "success");
    } catch (err) {
      showToast(err?.message || "Regeneration failed.", "error");
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleTogglePin = async () => {
    if (!selectedTopic) return;
    try {
      const updated = await updateTopic(selectedId, { pinned: !selectedTopic.pinned });
      setSelectedTopic(updated);
      setTopics((prev) => prev.map((t) => (t.id === updated.id ? { ...t, pinned: updated.pinned } : t)));
    } catch (err) {
      showToast(err?.message || "Failed to update pin.", "error");
    }
  };

  const handleDelete = async () => {
    if (!selectedTopic) return;
    if (!window.confirm(`Delete "${selectedTopic.title}"? This can't be undone.`)) return;
    try {
      await deleteTopic(selectedId);
      setTopics((prev) => prev.filter((t) => t.id !== selectedId));
      setSelectedId(null);
      setSelectedTopic(null);
      showToast("Topic deleted.", "success");
    } catch (err) {
      showToast(err?.message || "Delete failed.", "error");
    }
  };

  const handleCopyCheatSheet = async () => {
    if (!selectedTopic?.cheatSheet) return;
    try {
      await navigator.clipboard.writeText(selectedTopic.cheatSheet);
      showToast("Cheat sheet copied.", "success");
    } catch {
      showToast("Could not copy — select and copy manually.", "error");
    }
  };

  return (
    <div className="topics-page">
      <div className="topics-sidebar">
        <div className="topics-sidebar-header">
          <h2>🧠 Topics</h2>
          <button className="topics-new-btn" onClick={() => setShowNewModal(true)}>+ New</button>
        </div>

        <div className="topics-search-row">
          <input
            className="topics-search-input"
            placeholder="Search topics..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="topics-filter-row">
          <button
            className={`topics-filter-chip ${filterLabel === "All" ? "active" : ""}`}
            onClick={() => setFilterLabel("All")}
          >
            All
          </button>
          {labels.map((l) => (
            <button
              key={l}
              className={`topics-filter-chip ${filterLabel === l ? "active" : ""}`}
              style={filterLabel === l ? { "--chip-color": colorForLabel(l) } : undefined}
              onClick={() => setFilterLabel(l)}
            >
              {l}
            </button>
          ))}
        </div>

        <div className="topics-list">
          {loadingList && <div className="topics-loading">Loading...</div>}
          {!loadingList && filteredTopics.length === 0 && (
            <div className="topics-empty">
              {topics.length === 0
                ? "No topics yet. Ask about anything — a stock indicator, a tech concept, an ayurvedic remedy — and get a full explanation, references, and a revision cheat sheet."
                : "No topics match your filters."}
            </div>
          )}
          {filteredTopics.map((t) => (
            <div
              key={t.id}
              className={`topics-card ${selectedId === t.id ? "topics-card-active" : ""}`}
              style={{ "--card-color": colorForLabel(t.label) }}
              onClick={() => openTopic(t.id)}
            >
              <div className="topics-card-accent" />
              <div className="topics-card-body">
                <div className="topics-card-top-row">
                  {t.pinned && <span className="topics-pin-dot" title="Pinned">📌</span>}
                  <div className="topics-card-title">{t.title}</div>
                </div>
                {t.label && (
                  <span className="topics-label-pill" style={{ "--pill-color": colorForLabel(t.label) }}>
                    {t.label}
                  </span>
                )}
                {t.summary && <div className="topics-card-summary">{t.summary}</div>}
                <div className="topics-card-meta">
                  {t.viewCount > 0 && <span>👁 {t.viewCount}</span>}
                  {t.lastViewedAt && <span>· viewed {timeAgo(t.lastViewedAt)}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="topics-detail-panel">
        {!selectedId && (
          <div className="topics-hero-empty">
            <div className="topics-hero-icon">📚</div>
            <h3>Research anything, remember it forever</h3>
            <p>
              Type a topic — a trading strategy, a system-design concept, an ayurvedic herb, anything —
              and get a detailed explanation, real reference points, and an AI-written cheat sheet you can
              revisit whenever you need a refresher.
            </p>
            <button className="topics-btn-primary" onClick={() => setShowNewModal(true)}>+ New Topic</button>
          </div>
        )}

        {selectedId && loadingDetail && (
          <div className="topics-hero-empty">
            <div className="topics-hero-icon topics-spin">🔎</div>
            <h3>Loading...</h3>
          </div>
        )}

        {selectedId && !loadingDetail && selectedTopic && (
          <>
            <div className="topics-detail-header">
              <div className="topics-detail-header-top">
                <div>
                  {selectedTopic.label && (
                    <span className="topics-label-pill" style={{ "--pill-color": colorForLabel(selectedTopic.label) }}>
                      {selectedTopic.label}
                    </span>
                  )}
                  <h1>{selectedTopic.title}</h1>
                </div>
                <div className="topics-detail-actions">
                  <button
                    className={`topics-icon-btn ${selectedTopic.pinned ? "active" : ""}`}
                    title={selectedTopic.pinned ? "Unpin" : "Pin"}
                    onClick={handleTogglePin}
                  >
                    📌
                  </button>
                  <button
                    className="topics-icon-btn"
                    title="Regenerate"
                    onClick={handleRegenerate}
                    disabled={isRegenerating}
                  >
                    {isRegenerating ? "⏳" : "🔄"}
                  </button>
                  <button className="topics-icon-btn topics-icon-btn-danger" title="Delete" onClick={handleDelete}>
                    🗑
                  </button>
                </div>
              </div>
              <div className="topics-detail-meta">
                {selectedTopic.viewCount > 0 && <span>👁 viewed {selectedTopic.viewCount}×</span>}
                {selectedTopic.updatedAt && <span>· updated {timeAgo(selectedTopic.updatedAt)}</span>}
              </div>
              {selectedTopic.summary && (
                <div className="topics-summary-card">
                  <div className="topics-summary-label">TL;DR</div>
                  {selectedTopic.summary}
                </div>
              )}
            </div>

            <div className="topics-tabs">
              <button
                className={`topics-tab ${activeTab === "explanation" ? "active" : ""}`}
                onClick={() => setActiveTab("explanation")}
              >
                Explanation
              </button>
              <button
                className={`topics-tab ${activeTab === "cheatsheet" ? "active" : ""}`}
                onClick={() => setActiveTab("cheatsheet")}
              >
                Cheat Sheet
              </button>
              <button
                className={`topics-tab ${activeTab === "references" ? "active" : ""}`}
                onClick={() => setActiveTab("references")}
              >
                References
                {selectedTopic.references?.length > 0 && (
                  <span className="topics-tab-badge">{selectedTopic.references.length}</span>
                )}
              </button>
            </div>

            <div className="topics-tab-content">
              {activeTab === "explanation" && <LiteMarkdown text={selectedTopic.explanation} />}

              {activeTab === "cheatsheet" && (
                <>
                  <div className="topics-tab-toolbar">
                    <button className="topics-btn-secondary" onClick={handleCopyCheatSheet}>Copy</button>
                  </div>
                  <div className="topics-cheatsheet">
                    <LiteMarkdown text={selectedTopic.cheatSheet} />
                  </div>
                </>
              )}

              {activeTab === "references" && (
                <div className="topics-references">
                  {(!selectedTopic.references || selectedTopic.references.length === 0) && (
                    <div className="topics-empty-tab">No references were returned for this topic.</div>
                  )}
                  {selectedTopic.references?.map((r, i) => (
                    <div key={i} className="topics-reference-item">
                      <span className="topics-reference-title">{r.title}</span>
                      {r.url && (
                        <a href={r.url} target="_blank" rel="noreferrer" className="topics-reference-link">
                          {r.url}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {showNewModal && (
        <div className="topics-modal-overlay" onClick={() => !isCreating && setShowNewModal(false)}>
          <div className="topics-modal" onClick={(e) => e.stopPropagation()}>
            <div className="topics-modal-header">
              <h3>New Topic</h3>
              <button className="topics-modal-close" onClick={() => !isCreating && setShowNewModal(false)}>×</button>
            </div>
            <div className="topics-modal-body">
              <div className="topics-form-group">
                <label>What do you want to learn about?</label>
                <textarea
                  className="topics-textarea"
                  placeholder="e.g. RSI indicator for swing trading, Ashwagandha benefits and dosage, how Kafka partitions work..."
                  rows={4}
                  value={newTopicText}
                  onChange={(e) => setNewTopicText(e.target.value)}
                  disabled={isCreating}
                  autoFocus
                />
              </div>
              <div className="topics-form-group">
                <label>Label (optional — AI will suggest one if left blank)</label>
                <input
                  className="topics-input"
                  list="topics-label-suggestions"
                  placeholder="Stocks, Tech, Finance, Ayurveda..."
                  value={newTopicLabel}
                  onChange={(e) => setNewTopicLabel(e.target.value)}
                  disabled={isCreating}
                />
                <datalist id="topics-label-suggestions">
                  {labels.map((l) => <option key={l} value={l} />)}
                </datalist>
              </div>
              {isCreating && (
                <div className="topics-generating">
                  <div className="topics-spin">🔎</div>
                  Researching this topic — this can take up to 20 seconds...
                </div>
              )}
            </div>
            <div className="topics-modal-footer">
              <button className="topics-btn-secondary" onClick={() => setShowNewModal(false)} disabled={isCreating}>
                Cancel
              </button>
              <button className="topics-btn-primary" onClick={handleCreate} disabled={isCreating || !newTopicText.trim()}>
                {isCreating ? "Generating..." : "Generate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
